'use client'

import React, { useState, useCallback } from 'react'
import { toast, Gutter, SetStepNav, Banner, Button } from '@payloadcms/ui'

import './index.scss'

const CONFIRMATION_TEXT = 'run seeds and lose all saved data'

export const SeedManagementClient: React.FC = () => {
  const [confirmationInput, setConfirmationInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const isConfirmed = confirmationInput === CONFIRMATION_TEXT

  const handleSeed = useCallback(async () => {
    if (!isConfirmed || loading) return

    setLoading(true)
    setStatus('idle')

    try {
      const res = await fetch('/api/seed-management/seed', {
        method: 'POST',
        credentials: 'include',
      })

      if (res.ok) {
        setStatus('success')
        toast.success('Database seeded successfully!')
        setConfirmationInput('')
      } else {
        const data = await res.json()
        throw new Error(data.message || 'Failed to seed database')
      }
    } catch (err) {
      setStatus('error')
      toast.error(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, [isConfirmed, loading])

  const handleRollback = useCallback(async () => {
    if (!isConfirmed || loading) return

    setLoading(true)
    setStatus('idle')

    try {
      const res = await fetch('/api/seed-management/rollback', {
        method: 'POST',
        credentials: 'include',
      })

      if (res.ok) {
        setStatus('success')
        toast.success('Rollback completed successfully!')
        setConfirmationInput('')
      } else {
        const data = await res.json()
        throw new Error(data.message || 'Failed to rollback')
      }
    } catch (err) {
      setStatus('error')
      toast.error(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, [isConfirmed, loading])

  return (
    <>
      <SetStepNav nav={[{ label: 'Seed Management' }]} />
      <Gutter className="seed-management">
        <header className="seed-management__header">
          <h1>Seed Management</h1>
          <p className="seed-management__description">
            Manage database seeding and rollback operations for development and testing.
          </p>
        </header>

        <div className="seed-management__content">
          <Banner type="error">
            <strong>⚠️ DANGER ZONE</strong>
            <p>
              This action will <strong>delete all existing content</strong> and replace it with seed
              data. This cannot be undone.
            </p>
          </Banner>

          <div className="seed-management__form">
            <div className="seed-management__confirmation field-type text">
              <label className="field-label" htmlFor="confirmation-input">
                Type <code>{CONFIRMATION_TEXT}</code> to confirm:
              </label>
              <input
                id="confirmation-input"
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                placeholder="Type confirmation text..."
                disabled={loading}
                className="seed-management__input"
              />
            </div>

            <div className="seed-management__actions">
              <Button onClick={handleSeed} disabled={!isConfirmed || loading} buttonStyle="primary">
                {loading ? 'Processing...' : 'Run Seed'}
              </Button>
              <Button
                onClick={handleRollback}
                disabled={!isConfirmed || loading}
                buttonStyle="secondary"
              >
                {loading ? 'Processing...' : 'Rollback'}
              </Button>
            </div>

            {status === 'success' && (
              <Banner type="success">✓ Operation completed successfully</Banner>
            )}
            {status === 'error' && (
              <Banner type="error">✗ Operation failed. Check console for details.</Banner>
            )}
          </div>
        </div>
      </Gutter>
    </>
  )
}

export default SeedManagementClient
